import Tone from 'tone';
import {AUDIO} from 'constants/track-types.js';
import {clearObj} from 'utils/clearObj.js';
import {PALETTE_EFFECT} from 'effects/live/constants.js';
import {deleteSteps} from 'operations/actions.js';
import {
    setEffectParameterValues,
    setCurrentAndGlobalSteps,
} from 'actions/index.js';
import {triggerTracks} from './triggerTracks.js';
import {getTriggerTimes} from 'keyboard/TriggerTimeHelper.js';
import {sortTracksByDependencies} from 'effects/midi/scripting/dependencies.js';
import {publishRoll, publishTrigger} from 'messaging/messages.js';

const TIME_32N = new Tone.Time('32n');

export default superclass => class extends superclass  {

    // the main function called from the scheduling loop
    // schedules everything that should happen on this 1/32th step
    scheduleStep(time, currentStep) {
        time = this.handleLatency(time);
        let isFirstStep = this.storeStepAndTime(currentStep, time);

        // first handle any events that should occur in a "mix session"
        // a mix session contains any pattern switches that might occur
        this.handleMixSessionStep(time);

        // tell the H=O sequencer that a new step has occured
        this.higherOrderSequencer.onNewStep(this.trackStep, time);

        // apply any sequencing ordained by the H=O sequencer
        this.applyHigherOrderSequencing(this.trackStep);

        // make sure scenes knows about this new step
        this.scenes.onNewStep(this.trackStep);

        this.mixButtons.forEach(
            x => x.sendCurrentStepNumber(this.trackStep));

        // When playing several sequencer engines together, we 
        // want to sync the sequences so that they play in time with one another
        // this is used in the user page where they can play multiple sequences
        // and have them sync up.
        if (this.stepOneCallback !== undefined) {
            // HACK: why do we check different rawStep's depending on whether
            // we've startedTransposrtExplicitly? Needed for proper syncing tho.
            if (this.startedTransportExplicitly && currentStep == 15) {
                // stepOneCallback basically tells the other sequencer
                // engine to start
                this.stepOneCallback(time);
            } else if (!this.startedTransportExplicitly && currentStep == 0) {
                this.stepOneCallback(time);
            }
        }

        currentStep = this.trackStep; //  % this.length;

        if (this.scripting) {
            this.scripting.onStepReached(currentStep);
        }

        if (currentStep % this.length === 0) {
            this.scheduleAnyMasterResampling(time);
        }

        this.scheduleParametersToRecord(currentStep);

        if (isFirstStep) {
            this.scheduleParameterAutomations(currentStep, time, true);
        } else {
            if (this.isLooping && currentStep === this.loopStart) {
                this.scheduleParameterAutomations(currentStep, time, true);
            } else {
                this.scheduleParameterAutomations(currentStep, time, false);
            }
        }

        this.scheduleSubSequences(currentStep, time);
        // schedule the triggers for all the tracks in the project
        // metronome
        if (this.metronome) {
            this.scheduleTrack(this.metronome, currentStep, time)
                .forEach(
                    trigger => 
                        this.metronome.trigger(trigger.stepData, trigger.time, currentStep));
        }
        

        // if the samples are "rolling" then we must schedule any tracks
        // that are being "played" right now by the user
        this.scheduleSamplesRolling(currentStep, time);
        // todo: have this be an input to the midi effects
        // chain then finally play it

        currentStep = currentStep % this.length;

        let lastTrackStep = this.storeLastStepAndTime(currentStep, time);

        this.clockSync.onTick(this.getMilliseconds(time), currentStep);

        if (this.midi) {
            this.midi.onTick(this.getMilliseconds(time), currentStep, this.resolution); 
        }

        if (Object.keys(this.parametersToUpdate).length > 0) {
            this.dispatch(setEffectParameterValues(this.parametersToUpdate));
            this.parametersToUpdate = {};
        }

        Tone.context.setTimeout(() => {
            if (this.playing) {
                this.lastTrackStepReal = lastTrackStep;
                this.previousStepReal = this.trackStepReal;
                this.previousStepTimeReal = this.currentStepTime;
                this.trackStepReal = lastTrackStep;
                this.currentStepTime = time;
                this.store.dispatch(setCurrentAndGlobalSteps(this.getSequenceId(), currentStep, this.trackStep));
            }
        }, this.getMilliseconds(time)/1000.0);
    }

    inputTriggers = [];
    inputTracks = [];
    resolvedInputTriggers = [];
    otherModules = [];
    otherTriggers = [];

    scheduleSubSequences(currentStep, time) {
        // first need to schedule any sub-sequences are
        // set to route into the H=O sequencer
        let hOSequencer = this.higherOrderSequencer;
        let {inputModules} = hOSequencer;
        let {inputTriggers, inputTracks, resolvedInputTriggers, otherModules, otherTriggers} = this;

        inputTriggers.length = 0;
        inputTracks.length = 0;
        resolvedInputTriggers.length = 0;
        otherModules.length = 0;
        otherTriggers.length = 0;

        for (let subSequence of inputModules) {
            if (hOSequencer.shouldPlayModule(subSequence)) {
                inputTracks.push(subSequence.tracks);
                let _currentStep = hOSequencer.getStepForModule(
                    currentStep, subSequence);
                inputTriggers.push(this.scheduleTracks([...subSequence.tracks], _currentStep, time));
            }
        }

        inputTriggers.forEach(
            (triggers, i) => 
                resolvedInputTriggers.push(triggerTracks(this, inputTracks[i], triggers, time, currentStep)));

        let flattened = resolvedInputTriggers.flatMap(
            (t, i) => inputModules[i].tracks.flatMap(
                ({trackNumber})=> t[trackNumber]))
            .filter(x => x);

        hOSequencer.input(flattened);
        hOSequencer.output(time, currentStep);

        this.subSequences.forEach(
            s => {
                if (!inputModules.includes(s)) {
                    otherModules.push(s);
                }
            });

        let tracks = [];
        for (let subSequence of otherModules) {
            if (this.state.mix.isPlayingMix) {
                subSequence.updateVolumeFromAutomation(currentStep);
            }
            if (hOSequencer.shouldPlayModule(subSequence)) {
                let _currentStep = this.higherOrderSequencer.getStepForModule(
                    currentStep, subSequence);
                subSequence.schedulePads(time, _currentStep);
                this.scheduleSubSequenceZObjects(subSequence, time, _currentStep);
                let triggers = this.scheduleTracks(
                    [...subSequence.tracks], _currentStep, time);

                let outputModules = hOSequencer.getOutputForModule(
                    subSequence);
                for (let module of outputModules) {
                    otherTriggers.push(triggers);
                    tracks.push(module.tracks);
                }
            }
        }

        otherTriggers.forEach(
            (triggers, i) => 
                triggerTracks(this, tracks[i], triggers, time, currentStep));

        for (let sub of this.subSequences) {
            sub.sendPadMessages(time);
        }
    }

    scheduleSubSequenceZObjects(subSequence, time, patternStep) {

        let _patternStep = Math.floor(patternStep * subSequence.playhead.speedFactor);
        patternStep = _patternStep;
        if (subSequence.lastPatternStep === patternStep) {
            // we dont needa (we are in half time)
            return;
        }
        subSequence.lastPatternStep = patternStep;
        
        if (!this.scheduleSequenceRollingIfNeeded(subSequence, patternStep, time, true)) {
            // just do it normally...
            subSequence.scheduleZObjects(time, patternStep);
        } else {
            let resolution = this.resolution;
            let steps = resolution.getNumberOfStepsAtBaseResolution();
            let stepNumber = subSequence.patternRollStepNumber + patternStep % steps;
            if (!resolution.isTriplet) {
                // then trigger as normal, only using this calculated stepNumber
                subSequence.scheduleZObjects(time, stepNumber);
                return;
                //return this.getTrackTriggers(
                //    track, time, stepNumber, track.pattern, patternStep);
            }

            // triplets are a little trickier
            let _stepNumber = Math.floor(stepNumber);
            let off = (stepNumber - _stepNumber)*Tone.Time('32n');
            //if (track.isPlayheadReversed()) {
            //    off = -1 * off;
            //}
            stepNumber = _stepNumber
            let triggerTimes = getTriggerTimes(stepNumber, resolution, true);
            for (let trigger of triggerTimes) {
                subSequence.scheduleZObjects(time + trigger.time, stepNumber);
            }
        }
    }

    // schedules triggers for all the tracks in a sub-sequence
    scheduleTracks(tracks, currentStep, time) {
        if (tracks === undefined) {
            return undefined;
        }
        tracks = sortTracksByDependencies(tracks);

        let trackToTriggers = {};
        
        for (let track of tracks) {
            this.setGroupBits(track, currentStep);
        }
        
        for (var i=0; i < tracks.length; i++) {
            try {
                let track = tracks[i];
                let triggers = 
                    this.scheduleTrack(tracks[i], currentStep, time);
                trackToTriggers[track.trackNumber] = triggers;
                triggers.forEach(
                    x => publishTrigger(track, x));
            } catch (err) {
                console.log("caught error while scheduling track err = ");
                console.log(err);
            }
        }
        
        // for instrumnt rack's we need to call this onStepEnded
        // to ensure that any midi effects trigger
        // tracks.forEach(t => t.onStepEnded(time, currentStep));

        return trackToTriggers;
    }
    
    // schedules all the triggers/events to occur at this step 
    // for a given trak
    // todo: return triggers and process and then trigger
    scheduleTrack(track, currentStep, time) {
        if (track.disabled) {
            return [];
        }
        if (track.subSequence.isTrack) {
            track.scheduleTrackPatternChangeIfAny(currentStep);
        }
        
        let patternStep = track.getPatternStep(currentStep, true);

        // based on the pattern step send the pattern progress
        track.sendMessage("pattern progress", 100. * patternStep / track.length);

        let integerStep = Math.floor(patternStep);
        let timeOff = new Tone.Time('32n')*(patternStep-integerStep);

        let isTrackRecording = track.isEnabled;
        
        // schedule any pattern automations for this track
        try {
            track.pattern.mix.scheduleNext(integerStep, time, this);
        } catch (err) {
            console.log('caught an error while scheduling pattern events = ');
            console.log(err);
        }

        track.scheduleAnyRecording(patternStep, time);

        if (this.deleting) {
            track.deleteAtStep(integerStep);
        }
        
        if (integerStep == 0) {
            // when the patternStep is 0, we need to schedule
            // several things, because the pattern loop is "restarting"
            this.scheduleAnyFrozenTrackPlaying(track, time);
            this.scheduleAnyTrackRecording(track, time);
            this.scheduleAnyTrackResampling(track, time);
            this.scheduleAnyAudioTrackPlaying(track, time);
            this.scheduleAnyStepGroups(track, time);
        }

        // Depending on whether we are "sequence rolling" (i.e. beat repeating),
        // we need to schedule different track triggers
        if (!this.scheduleSequenceRollingIfNeeded(track, integerStep, time)) {
            // sequence is not rolling, so we schedule the usual track triggers
            if (!track.isMetronome && track.speedFactor > 1) {
                // if we're on double time or 4x time we need to actually get several
                // steps worth of triggers in this time slot
                let triggers = [];
                for (let i=1; i <= track.speedFactor; i++ ){
                    triggers = [
                        ... triggers,
                        ... this.getTrackTriggers(
                            track, time + timeOff, patternStep, track.pattern, patternStep
                        )];
                    time += new Tone.Time('32n');
                    patternStep++;
                }
                return triggers;
            }
            let triggers = this.getTrackTriggers(
                track,
                time + timeOff,
                patternStep,
                track.pattern,
                patternStep);
            return triggers;
        } else {
            // otherwise we schedule track triggers using the steps from when
            // te "sequence rolling" was first triggered
            let resolution = track.getFactoredResolution();
            let steps = resolution.getNumberOfStepsAtBaseResolution();
            let stepNumber = track.patternRollStepNumber + patternStep % steps;
            stepNumber = stepNumber % track.pattern.stepBuckets.length;
            if (!resolution.isTriplet) {
                // then trigger as normal, only using this calculated stepNumber
                return this.getTrackTriggers(
                    track, time, stepNumber, track.pattern, patternStep);
            }

            // triplets are a little trickier
            let _stepNumber = Math.floor(stepNumber);
            let off = (stepNumber - _stepNumber)*Tone.Time('32n');
            if (track.isPlayheadReversed()) {
                off = -1 * off;
            }
            stepNumber = _stepNumber
            let triggerTimes = getTriggerTimes(stepNumber, resolution, true);
            let triggers = [];
            let i=0;
            for (let triggerTime of triggerTimes) {
                triggers = [
                    ... triggers,
                    ... this.getTrackTriggers(
                        track, time - off + triggerTime.time, stepNumber, track.pattern, patternStep, i === 0)];
                i++;
            } 
            if (triggers.length === 0) {
                return this.getTrackTriggers(
                    track, time - off, stepNumber, track.pattern, patternStep);
            }
            return triggers;
        }

    }

    // schedules any samples, midi, audio to be triggered at this time,
    // for this track. If you're looking for where the sequencerEngine
    // actually triggers samples, this is it.
    getTrackTriggers(track, time, sampleStep, pattern, actualStep, first=true) {
        let isReversed = track.isPlayheadReversed(actualStep);
        var bucket = pattern.getBucket(sampleStep);
        if (first) {
            track.onNewStep(sampleStep, time);
        }

        let steps = bucket ? bucket.steps : [];
        let toDelete = [];

        if (track.isFrozen || track.getTrackType() === AUDIO) {
            if (sampleStep === 0) {
                track.triggerFrozenSample(time);
            }
        } else {
            // go through all steps inside this bucket (may contain unquantized
            // events, or more granular events...)

            for (var timeInBucket in steps) {
               // get the step we need to schedule
                let stepData = steps[timeInBucket]; 
                if (isReversed) {
                    timeInBucket = -1 * timeInBucket;
                }

                // calculate the time to play this step, based on the
                // bucket's time, and the current time.
                // Note: a bucket's key is a ratio of a 1/32th step. For example,
                // a key of 0.5 means we play this step 1/64th from now.
                let stepTime = new Tone.Time(time) + (TIME_32N * timeInBucket);
                
                // there may be several steps inside this step, i.e. a polyphonic
                // chord of individual StepData objects, to each be played
                let polyphony = stepData.getPolyphony();

                // order the triggers so slide come last
                polyphony = [... polyphony.filter(x => !x.slide), ... polyphony.filter(x => x.slide)];

                for (let i in polyphony) { 
                    let polyphonicStep = polyphony[i];
                    if (track.deleting) {
                        toDelete.push(polyphonicStep);
                        continue;
                    }

                    // based on the probability parameters of both
                    // the step and any groups the step is a part of,
                    // mask the step 
                    if (this.isStepMasked(polyphonicStep)) {
                        continue;
                    }

                    // first transform the step based on the step groups
                    let {time, step} = this.transformStep(polyphonicStep, stepTime, actualStep);
                    if (!step.slide) {
                        // apply p-locks
                        track.handlePLockedAutomations(step, time);
                    }
                    // send it through the midi effects 
                    track.midiEffects.input(
                        step, time, actualStep);
                }
            }
        }

        if (track.deleting) {
            if (toDelete.length > 0) {
                this.operationManager.dispatch(deleteSteps(
                    track, toDelete));
            }
            return [];
        }

        // finally trigger the events that the midi effects chain
        // outputed
        // todo: figure out what to do about lost notes
        let _triggers = track.midiEffects.output(time, sampleStep, actualStep);


        // make sure that they contain a "track" associated with it
        let triggers= _triggers
            .filter(x => x.stepData && x.stepData.track);

        if (!this.shouldPlayStep(actualStep)) {
            return [];
        }

        return triggers;
    }

    storeLastStepAndTime(currentStep, time) {
        // now set a bunch of fields telling what step and time
        // we currently are at:
        // TODO: do we really need 10+ variables for all of this?
        this.nextStepScheduled = currentStep;
        let newTrackStep = this.getLoopedStep(this.trackStep + 1);
        let lastTrackStep = this.trackStep;
        this.lastTrackStep = this.trackStep;
        this.trackStep = newTrackStep;
        this.previousStepTime = this.latestStepTime;
        this.latestStepTime = time;
        let previousStepTime = new Tone.Time(this.latestStepTime);
        let previousStep = this.currentStep;
        this.currentStep = currentStep;
        this.previousStep = previousStep;
        return lastTrackStep;
    }


    storeStepAndTime(currentStep, time) {
        let isFirstStep = this.firstStep;
        if (this.firstStep) {
            for (let sub of this.subSequences) {
                sub.lfosTrack.start(time);
                sub.tracks.filter(x => x.lfosTrack)
                    .forEach(x => x.lfosTrack.start(time));
            }
            this.firstStep = false;
        }

        // here is a hack, where if the lastTrackStep that has been scheduled
        // is the same as this track. In this case, we add 1. This can cause
        // problems. Seems to be working though!
        if (this.lastTrackStep === this.trackStep) {
            this.trackStep += 1;
        }
        
        this.nextTimeScheduled = time;

        // half of these aren't used, was trying to nail automation timing
        let automationStep =  this.automationStep;
        this.automationStep = this.getLoopedStep(this.trackStep);

        return isFirstStep;
    }

    shouldPlayStep(stepNumber) {
        stepNumber = stepNumber % 32;
        stepNumber = Math.floor(stepNumber / 2);
        return this.state.steps.masterStepToggles[stepNumber];
    }
}
